import { FC, useMemo, useState } from "react"
import { ThresholdType } from "@/api/AlertsService"
import AlertThresholdSettings, {
   ThresholdGroup,
} from "@/components/AlertThresholdSetting"
import Loader from "@/components/Loader"
import GridItem from "@/components/shared/Grid/GridItem"
import SnackBar from "@/components/shared/SnackBar"
import { SpinnerSize } from "@/components/shared/Spinner"
import { useDynamicThresholdsEnabled } from "@/hooks/useDynamicThresholdsEnabled"
import { AlertThreshold } from "@/types/alert"
import { AlertConfigurationForm } from "@/types/alertConfigurations"

interface ThresholdProps {
   disabled?: boolean
   type: ThresholdType
   loading: boolean
   sensorType?: boolean
   wearableType?: boolean
   dynamicToggleDisabled?: boolean
   form?: AlertThreshold
   onSave: (args: AlertConfigurationForm["thresholds"]) => void
}

const Threshold: FC<ThresholdProps> = ({
   disabled = false,
   type,
   loading,
   form = {} as AlertThreshold,
   sensorType,
   wearableType,
   dynamicToggleDisabled,
   onSave,
}) => {
   const title = useMemo(() => {
      switch (type) {
         case ThresholdType.HEART_RATE:
            return "Heart rate"
         case ThresholdType.RESPIRATORY_RATE:
            return "Respiratory rate"
         case ThresholdType.OXYGEN_LEVEL:
            return "Oxygen level"
         default:
            return ""
      }
   }, [type])

   const iconName = useMemo(() => {
      switch (type) {
         case ThresholdType.HEART_RATE:
            return "heartRate"
         case ThresholdType.RESPIRATORY_RATE:
            return "respiratoryRate"
         case ThresholdType.OXYGEN_LEVEL:
            return "oxygenLevel"
      }
   }, [type])

   const thresholdGroups = useMemo<ThresholdGroup<AlertThreshold>[]>(
      () => [
         {
            iconName: "upperThreshold",
            label: "High threshold",
            limitLabel: "Limit (% above avg.)",
            key: "high",
            durationKey: "highThresholdDuration",
         },
         {
            iconName: "lowerThreshold",
            label: "Low threshold",
            limitLabel: "Limit (% below avg.)",
            key: "low",
            durationKey: "lowThresholdDuration",
            isLow: true,
         },
      ],
      []
   )

   const dynamicThresholdsEnabled = useDynamicThresholdsEnabled()

   const toggleable = useMemo(() => {
      if (!dynamicThresholdsEnabled) return false
      switch (type) {
         case ThresholdType.HEART_RATE:
            return Boolean(sensorType || wearableType)
         case ThresholdType.RESPIRATORY_RATE:
            return Boolean(sensorType)
         default:
            return false
      }
   }, [type, sensorType, wearableType, dynamicThresholdsEnabled])

   const [isDynamicSnackbarOpen, setIsDynamicSnackbarOpen] = useState(false)

   const handleSave = (state: AlertThreshold) => {
      onSave([
         {
            type,
            processingMethod: state.processingMethod,
            high: { value: state.high, duration: state.highThresholdDuration },
            low: { value: state.low, duration: state.lowThresholdDuration },
         },
      ])
   }

   return (
      <>
         <GridItem size={2}>
            {loading ? (
               <div className="alert-setting-detail--loading">
                  <Loader
                     size={SpinnerSize.SMALL}
                     fullHeight={false}
                  />
               </div>
            ) : (
               <AlertThresholdSettings
                  disabled={disabled}
                  title={title}
                  iconName={iconName}
                  extremeThreshold={1000}
                  thresholdGroups={thresholdGroups}
                  form={form}
                  toggleable={toggleable}
                  dynamicToggleDisabled={dynamicToggleDisabled}
                  onDynamicChangeSaved={() => setIsDynamicSnackbarOpen(true)}
                  onSave={handleSave}
               />
            )}
         </GridItem>
         <SnackBar
            isOpen={isDynamicSnackbarOpen}
            message="Dynamic thresholds will become active 30 minutes after the first reading is received"
            onClose={() => setIsDynamicSnackbarOpen(false)}
         />
      </>
   )
}

export default Threshold
